"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CinematicPresence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = [{
    text: "Capturing",
    italic: false,
    bold: false,
    accent: false
  }, {
    text: "truth",
    italic: true,
    bold: true,
    accent: true
  }, {
    text: "within",
    italic: false,
    bold: false,
    accent: false
  }, {
    text: "the",
    italic: false,
    bold: false,
    accent: false
  }, {
    text: "frame",
    italic: true,
    bold: false,
    accent: false
  }, {
    text: "forever.",
    italic: true,
    bold: true,
    accent: false
  }];
  const stats = [{
    label: "FILM & TV"
  }, {
    label: "STAGE CREDITS"
  }, {
    label: "EQUITY ACTOR"
  }];
  return <section data-section-id="714" id="cinematic-presence" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1); }
          50% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes wordReveal {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .ken-burns {
          animation: kenburns 15s ease-in-out infinite;
        }
        .spin-slow {
          animation: spinSlow 20s linear infinite;
        }
        .word-reveal {
          display: inline-block;
          opacity: 0;
          animation: wordReveal 0.8s ease-out forwards;
        }
      `}</style>

      <div ref={ref} className="relative grid min-h-screen grid-cols-1 lg:grid-cols-12">
        {}
        <div className="relative z-10 flex flex-col justify-center bg-background px-6 py-16 sm:px-10 md:px-12 lg:col-span-7 lg:px-16 lg:py-24 order-2 lg:order-1">
          {}
          <div className="mb-8 flex items-center gap-3" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.6s ease-out"
        }}>
            <div className="h-px w-10 bg-border" />
          </div>

          {}
          <h1 className="font-default text-foreground" style={{
          fontSize: "clamp(3rem, 8vw, 6.5rem)",
          lineHeight: 1.0,
          letterSpacing: "-0.02em"
        }}>
            {headlineWords.map((word, i) => <span key={i} data-index={i} className="word-reveal mr-[0.25em] inline-block" style={{
            animationDelay: `${i * 100 + 200}ms`,
            fontStyle: word.italic ? "italic" : "normal",
            fontWeight: word.bold ? 500 : 300,
            color: word.accent ? "var(--primary)" : "var(--foreground)",
            WebkitTextStroke: word.accent ? "0px" : "none"
          }}>
                {word.text}
              </span>)}
          </h1>

          {}
          <p className="mt-10 max-w-[480px] font-sans text-lg leading-relaxed" style={{
          color: "var(--smoke-glass)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.8s ease-out 1s"
        }}>
            Jordan Rivers is a film and theater actor dedicated to the visceral 
            exploration of the human condition. From dramatic production stills 
            to the intensity of the stage.
          </p>

          {}
          <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.8s ease-out 1.2s"
        }}>
            <Button asChild size="lg" className="group h-14 rounded-md border border-starlight-silver bg-transparent px-8 text-base font-medium text-foreground hover:bg-primary hover:text-background transition-all duration-300">
              <Link to="/TheLedgerPage">
                View Credits
                <ArrowRight className="ml-1 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>

            <Link to="/VisualArchivePage" className="group relative inline-flex items-center font-sans text-base font-medium text-foreground">
              <span className="tracking-wider">Visual Archive</span>
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-border transition-transform duration-500 group-hover:scale-x-0" />
              <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-primary transition-transform duration-500 delay-150 group-hover:scale-x-100" />
            </Link>
          </div>

          {}
          <div className="mt-16 flex flex-wrap items-center gap-x-6 gap-y-4 sm:gap-x-10" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.8s ease-out 1.4s"
        }}>
            {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-x-6 sm:gap-x-10">
                {i > 0 && <div className="h-4 w-px" style={{
              backgroundColor: "var(--border)"
            }} />}
                <span className="font-mono text-xs uppercase tracking-[0.2em]" style={{
              color: "var(--smoke-glass)"
            }}>
                  {stat.label}
                </span>
              </div>)}
          </div>
        </div>

        {}
        <div className="relative h-[60vh] overflow-hidden lg:col-span-5 lg:h-auto lg:min-h-screen order-1 lg:order-2">
          <div className="absolute inset-0 ken-burns">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_dramatic_headshot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_dramatic_headshot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_dramatic_headshot.png-wvc-sizes" alt="High-contrast black and white headshot of actor Jordan Rivers with intense focus" loading="lazy" decoding="async" className="h-full w-full object-cover grayscale" />
          </div>

          {}
          <div className="pointer-events-none absolute inset-0" style={{
          background: "linear-gradient(to right, var(--background) 0%, transparent 40%)"
        }} />
          <div className="pointer-events-none absolute inset-0 lg:hidden" style={{
          background: "linear-gradient(to bottom, transparent 60%, var(--background) 100%)"
        }} />

          {}
          <div className="absolute right-6 top-6 sm:right-10 sm:top-10 lg:right-12 lg:top-12">
            <div className="relative size-28 sm:size-32 lg:size-36">
              <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full" style={{
              color: "var(--smoke-glass)"
            }}>
                <defs>
                  <path id="circle-text-path" d="M 100,100 m -75,0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" />
                </defs>
                <text className="font-mono" style={{
                fontSize: "14px",
                letterSpacing: "0.3em",
                fill: "currentColor"
              }}>
                  <textPath href="#circle-text-path">
                    ISO 800 • 24FPS • 4K •
                  </textPath>
                </text>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="size-1 rounded-md" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}